<script setup>
import { reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const form = reactive({
  channelName: '',
  appId: '',
  appSecret: '',
});

const rules = {
  channelName: { required },
  appId: { required },
  appSecret: { required },
};

const v$ = useVuelidate(rules, form);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const douyinChannel = await store.dispatch('inboxes/createChannel', {
      name: form.channelName?.trim(),
      channel: {
        type: 'douyin',
        app_id: form.appId,
        app_secret: form.appSecret,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: douyinChannel.id,
      },
    });
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="form.channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">{{
            $t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.appId.$error }">
          {{ $t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.APP_ID.LABEL') }}
          <input
            v-model="form.appId"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.APP_ID.PLACEHOLDER')
            "
            @blur="v$.appId.$touch"
          />
          <span v-if="v$.appId.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.APP_ID.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.appSecret.$error }">
          {{ $t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.APP_SECRET.LABEL') }}
          <input
            v-model="form.appSecret"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.APP_SECRET.PLACEHOLDER')
            "
            @blur="v$.appSecret.$touch"
          />
          <span v-if="v$.appSecret.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.APP_SECRET.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.DOUYIN_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
